"use client";

/**
 * Record Page
 *
 * Audio/video recorder with live transcription, timer, and upload.
 * Video mode shows a live camera viewfinder before recording starts.
 *
 * Route: /upload/record
 */

import { useState, useCallback, useRef, useEffect, type RefObject } from "react";
import { z } from "zod";
import { useNavigate, useSearchParams, useBlocker, type BlockerFunction } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAppStore } from "@/stores";
import { Button } from "@speakai/ui/button";
import { Switch } from "@speakai/ui/switch";
import { Skeleton } from "@speakai/ui/skeleton";
import { FolderSelect } from "@/features/folder/components/folder-select";
import { CircleCheck, Mic } from "lucide-react";
import { RECORD_MODES } from "@/features/recorder/constants";
import { ConfirmDialog } from "@speakai/ui/confirm-dialog";
import { PageHeader } from "@speakai/ui/page-header";
import { cn } from "@/lib/utils/cn";
import { useTranslation } from "react-i18next";
import { useFolderList } from "@/features/folder/queries";
import { useRecording } from "@/features/recorder/hooks/use-recording";
import { useLiveTranscript } from "@/features/recorder/hooks/use-live-transcript";
import { useLiveTranscriptionToken } from "@/features/recorder/queries";
import { isScreenCaptureSupported } from "@/features/recorder/utils/screen-capture-support";
import { QUALITY_CONSTRAINTS } from "@/features/recorder/hooks/use-recording-engine";
import { useBrowserRecordingSupport } from "@/features/recorder/hooks/use-browser-recording-support";
import { extensionForBlob, MIME_TO_EXTENSION } from "@speakai/common/src/recorder/core/negotiation/mime-extension";
import { useAuth } from "@/hooks/use-auth";
import { resolveDefaultUploadFolderId, getLastUploadFolderId } from "@/features/upload/default-folder";
import { useSubscription } from "@/features/subscription/queries";
import { SubscriptionPlanId } from "@/features/subscription/types";
import { FREE_PLAN_MAX_FILE_SIZE } from "@/features/upload/supported-formats";
import { MediaRecorderControls } from "./components/media-recorder";
import { PhoneAppNote } from "./components/phone-app-note";
import { LanguageSelect } from "./components/recorder/language-select";
import { trackPageview, trackFunnel, trackFailure } from "@/lib/integrations/sentry-tracking";
import { trackActivity } from "@/lib/integrations/activity-events";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { uploadRecordingToS3 } from "./cognito-upload";
import type { S3UploadProgress } from "./cognito-upload";
import { uploadRecording } from "@/features/media/api";
import { useDeleteMedia } from "@/features/media/mutations";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useToast } from "@speakai/ui/toast";
import { deleteTake, listRecoverableTakes, loadTakeBlob } from "@/features/recorder/take-store";
import type { RecordingMode, SavedTake } from "@/features/recorder/types";
import { downloadBlob } from "@/lib/utils/download-blob";
import { RecoverTakeBanner } from "./components/recorder/recover-take-banner";

const RECORDING_EXTENSION_PATTERN = new RegExp(`\\.(${MIME_TO_EXTENSION.map(([, extension]) => extension).join("|")})$`, "i");

/** `{mode}-{YYYYMMDD-HHmmss}.{ext}` for a take started at `date`. */
function defaultRecordingName(mode: RecordingMode, date: Date, blob: Blob | null): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const ts = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `${mode}-${ts}.${extensionForBlob(blob)}`;
}

const recordingFormSchema = z.object({
  recordingName: z.string().trim().min(1, "Recording name is required"),
  folderId: z.string().min(1, "Please choose a folder"),
  sourceLanguage: z.string().min(1, "Please choose a language"),
});


export function RecordPageContent({ isDialog = false, onNavigate }: {
  isDialog?: boolean;
  onNavigate?: (target: "folder" | "file" | "dashboard", id?: string, folderId?: string) => void;
} = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const folderIdParam = searchParams.get("folderId") ?? "";
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: subscription } = useSubscription();

  useEffect(() => {
    if (!isDialog) trackPageview("upload/record");
  }, [isDialog]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<"audio" | "video" | "screen">("audio");
  const isVideo = mode === "video" || mode === "screen";
  // Camera preview is only for video mode — screen mode acquires camera at recording start
  const isCameraPreviewMode = mode === "video";
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [uploadedMediaId, setUploadedMediaId] = useState<string | null>(null);
  const pendingRecording = useAppStore((s) => s.pendingRecording);
  const setPendingRecording = useAppStore((s) => s.setPendingRecording);
  const [recordingName, setRecordingName] = useState(
    pendingRecording?.name ?? ""
  );
  const [folderId, setFolderId] = useState("");
  const [sourceLanguage, setSourceLanguage] = useState(
    pendingRecording?.language ?? user?.onboarding?.sourceLanguage ?? "en-US"
  );
  const [isLiveTranscription, setIsLiveTranscription] = useState(false);
  const [liveFolderId, setLiveFolderId] = useState("");

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [videoQuality, setVideoQuality] = useState("720p");
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<S3UploadProgress | null>(null);
  const [showMaxDurationWarning, setShowMaxDurationWarning] = useState(false);

  const screenSupported = isScreenCaptureSupported().supported;
  const browserSupport = useBrowserRecordingSupport();

  // Default off — avoids an unexpected camera permission prompt on first screen-mode use.
  const [withCameraOverlay, setWithCameraOverlay] = useState(false);

  const { data: folderListData, isLoading: isFolderLoading } = useFolderList({ pageSize: 200 });
  const folders = folderListData?.folders ?? [];

  useEffect(() => {
    if (folders.length === 0 || folderId) return;
    // Pre-select the folder passed via ?folderId= if it exists in the list;
    // otherwise fall back to the first folder ("My Files").
    const matchedFolder = folderIdParam
      ? folders.find((f) => f.folderId === folderIdParam)
      : undefined;
    const defaultFolderId =
      matchedFolder?.folderId ??
      resolveDefaultUploadFolderId(folders, getLastUploadFolderId(user?._id)) ??
      "";
    if (defaultFolderId) {
      setFolderId(defaultFolderId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);

  const handleModeClick = useCallback((newMode: "audio" | "video" | "screen") => {
    setMode(newMode);
    if (newMode !== "audio") {
      setIsLiveTranscription(false);
    }
  }, []);

  const autoStartAttemptedRef = useRef(false);
  useEffect(() => {
    if (pendingRecording && !autoStartAttemptedRef.current) {
      autoStartAttemptedRef.current = true;
      setPendingRecording(null);

      // getDisplayMedia requires a live user gesture — never auto-start screen mode.
      if (mode === "screen") return;

      // Promise chain catches async rejections from startRecording() (wraps getUserMedia).
      Promise.resolve()
        .then(() => recording.startRecording())
        .catch((err) => {
          // NotAllowedError/NotSupportedError are expected on iOS Safari or when
          // getUserMedia is invoked outside a direct gesture handler — fall through
          // to manual start. Re-throw anything else so it surfaces in Sentry.
          if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "NotSupportedError")) {
            return;
          }
          throw err;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRecording]);

  const previewStreamRef = useRef<MediaStream | null>(null);
  // Forwarded to the playback element for setSinkId speaker routing after recording.
  const mediaRef = useRef<HTMLAudioElement | HTMLVideoElement | null>(null);

  const liveTranscriptionEnabled = isLiveTranscription && mode === "audio";
  const { data: liveToken, isError: liveTokenError } =
    useLiveTranscriptionToken(liveTranscriptionEnabled);
  const apiKey = liveToken ?? "";

  const liveTranscript = useLiveTranscript({
    apiKey,
    sourceLanguage,
    mediaType: mode === "audio" ? "audio" : "video",
    enabled: liveTranscriptionEnabled && !!apiKey,
    folderId: liveFolderId,
  });

  const { mutate: deleteLiveMedia } = useDeleteMedia();

  // Read after the upload finishes, so a live media created while it ran is included.
  const takeMediaIdsRef = useRef(liveTranscript.takeMediaIds);
  useEffect(() => {
    takeMediaIdsRef.current = liveTranscript.takeMediaIds;
  }, [liveTranscript.takeMediaIds]);

  const liveTranscriptIsConnectedRef = useRef(liveTranscript.isConnected);
  useEffect(() => {
    liveTranscriptIsConnectedRef.current = liveTranscript.isConnected;
  }, [liveTranscript.isConnected]);

  // Ref so awaitSocketReady can read the current error without a stale closure.
  const liveTranscriptErrorRef = useRef(liveTranscript.error);
  useEffect(() => {
    liveTranscriptErrorRef.current = liveTranscript.error;
  }, [liveTranscript.error]);

  const baseRecording = useRecording({
    mode,
    isVideo,
    withCameraOverlay: mode === "screen" ? withCameraOverlay : false,
    onAudioData: liveTranscript.sendAudioData,
    videoQuality,
    getSocketState: isLiveTranscription ? () => liveTranscriptIsConnectedRef.current : undefined,
    getSocketError: isLiveTranscription ? () => liveTranscriptErrorRef.current : undefined,
    userId: user?._id,
  });

  const { isRecording, isReadyToSubmit, autosave } = baseRecording;

  const toast = useToast();
  const [savedTakes, setSavedTakes] = useState<SavedTake[]>([]);
  const [busyTakeId, setBusyTakeId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?._id) return;
    let cancelled = false;
    listRecoverableTakes(user._id)
      .then((takes) => {
        if (!cancelled) setSavedTakes(takes);
      })
      .catch((err) => Sentry.captureException(err, { tags: { feature: "recorder" } }));
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  const runSavedTakeAction = useCallback(
    async (take: SavedTake, action: () => Promise<void>) => {
      setBusyTakeId(take.id);
      try {
        await action();
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: "recorder" } });
        toast.error(t("UPLOAD.RECORD.RECOVER_FAILED", "Something went wrong with this saved recording. Please try again."));
      } finally {
        setBusyTakeId(null);
      }
    },
    [toast, t],
  );

  const handleRecoverUpload = useCallback(
    (take: SavedTake) =>
      runSavedTakeAction(take, async () => {
        await baseRecording.restoreTake(take);
        setMode(take.mode);
        setIsLiveTranscription(false);
        setSavedTakes((prev) => prev.filter((saved) => saved.id !== take.id));
        trackFunnel("in_app_recorder", "recover", { mode: take.mode, duration_seconds: take.durationSec });
      }),
    [runSavedTakeAction, baseRecording],
  );

  const handleRecoverDownload = useCallback(
    (take: SavedTake) =>
      runSavedTakeAction(take, async () => {
        const blob = await loadTakeBlob(take);
        downloadBlob(blob, defaultRecordingName(take.mode, new Date(take.startedAt), blob));
      }),
    [runSavedTakeAction],
  );

  const handleRecoverDiscard = useCallback(
    (take: SavedTake) =>
      runSavedTakeAction(take, async () => {
        await deleteTake(take.id);
        setSavedTakes((prev) => prev.filter((saved) => saved.id !== take.id));
        trackFunnel("in_app_recorder", "recover_discard", { mode: take.mode });
      }),
    [runSavedTakeAction],
  );

  useEffect(() => {
    if (!isRecording && !isReadyToSubmit) setLiveFolderId(folderId);
  }, [folderId, isRecording, isReadyToSubmit]);

  const stopPreview = useCallback(() => {
    if (previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach((t) => t.stop());
      previewStreamRef.current = null;
    }
    setPreviewStream(null);
  }, []);

  const startCameraPreview = useCallback(async (): Promise<MediaStream | null> => {
    const sizeConstraints = QUALITY_CONSTRAINTS[videoQuality] ?? {};
    const videoConstraints: MediaTrackConstraints = baseRecording.selectedVideoDevice
      ? { deviceId: { exact: baseRecording.selectedVideoDevice }, ...sizeConstraints }
      : sizeConstraints;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
      previewStreamRef.current = stream;
      setPreviewStream(stream);
      setHasPermission(true);
      return stream;
    } catch {
      setHasPermission(false);
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoQuality, baseRecording.selectedVideoDevice]);

  // Screen mode never requests camera pre-recording; the hook acquires it at start.
  useEffect(() => {
    if (isCameraPreviewMode && !isRecording && !isReadyToSubmit) {
      void startCameraPreview();
    } else {
      stopPreview();
    }
    return () => {
      stopPreview();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraPreviewMode, isRecording, isReadyToSubmit, stopPreview, startCameraPreview]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (baseRecording.isRecording || isSubmitting || isReadyToSubmit) {
        e.preventDefault();
        // Modern browsers require a non-undefined returnValue to show the dialog; empty string is the spec recommendation.
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [baseRecording.isRecording, isSubmitting, isReadyToSubmit]);

  // SPA navigation guard. Patching history.pushState cannot work here: React Router notifies its
  // subscribers regardless of whether pushState ran, so the route still unmounts and the recording
  // dies. useBlocker holds the transition itself, so reset() genuinely keeps the user on the page.
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        (baseRecording.isRecording || isSubmitting || isReadyToSubmit) &&
        currentLocation.pathname !== nextLocation.pathname,
      [baseRecording.isRecording, isSubmitting, isReadyToSubmit],
    ),
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    let title: string;
    let message: string;
    if (isSubmitting) {
      title = t("UPLOAD.RECORD.LEAVE_UPLOAD_TITLE", "Upload in progress");
      message = t("UPLOAD.RECORD.LEAVE_UPLOAD_MESSAGE", "Your recording is currently being uploaded. Leaving now will cancel the upload.");
    } else {
      const recoverableMessage = t("UPLOAD.RECORD.LEAVE_UNSAVED_MESSAGE", "Your recording hasn't been uploaded yet. If you leave, you can recover it from the record page for 7 days.");
      if (isRecording) {
        title = t("UPLOAD.RECORD.LEAVE_RECORDING_TITLE", "Recording in progress");
        message = autosave === "on"
          ? recoverableMessage
          : t("UPLOAD.RECORD.LEAVE_RECORDING_MESSAGE", "You have an active recording in progress. Leaving now will discard it.");
      } else {
        title = t("UPLOAD.RECORD.LEAVE_UNSAVED_TITLE", "Recording not uploaded");
        message = autosave === "on"
          ? recoverableMessage
          : t("UPLOAD.RECORD.LEAVE_UNSAVED_LOST_MESSAGE", "Your recording hasn't been uploaded yet and couldn't be saved in this browser. Leaving now will discard it.");
      }
    }
    // eslint-disable-next-line no-alert
    if (window.confirm(`${title}\n\n${message}`)) blocker.proceed?.();
    else blocker.reset?.();
  }, [blocker, isSubmitting, isRecording, autosave, t]);

  const WARNING_THRESHOLD_SECONDS = 14400 - 60; // 60 s before the 4-hour hard limit

  useEffect(() => {
    if (isRecording && baseRecording.elapsedTime >= WARNING_THRESHOLD_SECONDS) {
      setShowMaxDurationWarning(true);
    } else if (!isRecording) {
      setShowMaxDurationWarning(false);
    }
  }, [isRecording, baseRecording.elapsedTime]);

  // Server already has the full media after live transcription unless audio went undelivered; then the blob stays for upload.
  useEffect(() => {
    if (isLiveTranscription && isReadyToSubmit && !submitSuccess && !isSubmitting && !liveTranscript.hasUndeliveredAudio) {
      baseRecording.discardRecording();
      trackFunnel("in_app_recorder", "submit");
      setSubmitSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveTranscription, isReadyToSubmit, submitSuccess, isSubmitting, liveTranscript.hasUndeliveredAudio]);

  // Auto-fills `{mode}-{YYYYMMDD-HHmmss}.{ext}` once on recording complete; user edits win.
  const namePrefilledRef = useRef(false);
  const startingRef = useRef(false);
  useEffect(() => {
    if (!isReadyToSubmit || namePrefilledRef.current) return;
    if (recordingName.trim()) {
      namePrefilledRef.current = true;
      return;
    }
    setRecordingName(defaultRecordingName(mode, new Date(), baseRecording.blob));
    namePrefilledRef.current = true;
  }, [isReadyToSubmit, mode, recordingName, baseRecording.blob]);

  useEffect(() => {
    if (!isReadyToSubmit) namePrefilledRef.current = false;
  }, [isReadyToSubmit]);

  const recording = {
    ...baseRecording,
    startRecording: async (...args: Parameters<typeof baseRecording.startRecording>) => {
      if (startingRef.current || baseRecording.isRecording) return;
      startingRef.current = true;
      stopPreview();
      liveTranscript.clearWords();
      trackFunnel("in_app_recorder", "start", { mode });
      trackActivity("recording.start", { mode });
      pushDataLayer("App Recording", "Start Recording");
      // DOMExceptions from getUserMedia (permission denied, unsupported) are expected; swallow them.
      try {
        await baseRecording.startRecording(...args);
      } catch (err) {
        if (err instanceof DOMException) return;
        throw err; // unexpected — let Sentry catch it
      } finally {
        startingRef.current = false;
      }
    },
    stopRecording: () => {
      if (isLiveTranscription) {
        liveTranscript.stopTranscription();
      }
      trackFunnel("in_app_recorder", "stop", { mode, duration_seconds: baseRecording.elapsedTime });
      trackActivity("recording.stop", { mode, durationMs: baseRecording.elapsedTime * 1000 });
      baseRecording.stopRecording();
    },
    // Shows confirm dialog while recording or when a blob exists; otherwise discards immediately.
    discardRecording: () => {
      if (baseRecording.isRecording || baseRecording.blob) {
        setShowDiscardDialog(true);
      } else {
        trackFunnel("in_app_recorder", "reset", { mode });
        baseRecording.discardRecording();
      }
    },
  } as ReturnType<typeof useRecording>;

  // Screen mode never requests camera pre-recording.
  const handleRequestPermission = useCallback(() => {
    setHasPermission(null);
    if (isCameraPreviewMode) {
      stopPreview();
      void startCameraPreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraPreviewMode, stopPreview, startCameraPreview]);

  const handleSubmit = useCallback(async () => {
    if (!recording.blob || !user) return;

    // Validate required fields before starting the upload pipeline.
    const validation = recordingFormSchema.safeParse({
      recordingName: recordingName.trim(),
      folderId,
      sourceLanguage,
    });
    if (!validation.success) {
      setSubmitError(validation.error.issues[0]?.message ?? t("UPLOAD.RECORD.FILL_REQUIRED", "Please fill in all required fields."));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setUploadProgress(null);
    pushDataLayer("App Recording", "Submit Recording");

    try {
      const name = recordingName.trim() || `Recording ${new Date().toLocaleString()}`;
      const extension = extensionForBlob(recording.blob);
      // Strip existing extension before re-appending to avoid double-suffix (e.g. "name.webm.webm").
      const stem = name.replace(RECORDING_EXTENSION_PATTERN, "");
      const filename = `${stem.replace(/[^a-zA-Z0-9]/g, "_")}.${extension}`;

      const { promise } = uploadRecordingToS3(
        recording.blob,
        {
          userId: user._id,
          filename,
          mediaType: mode === "audio" ? "audio" : "video",
          bucketId: subscription?.bucketId,
          maxBytes:
            subscription?.planId === SubscriptionPlanId.Speak_Free_Trial && !subscription?.isExpired
              ? FREE_PLAN_MAX_FILE_SIZE
              : undefined,
          onProgress: (p) => setUploadProgress(p),
        },
      );
      const result = await promise;

      if (result.status !== "success") {
        setSubmitError((result.message ?? result.error) as string | null ?? t("UPLOAD.RECORD.UPLOAD_FAILED", "Upload failed"));
        return;
      }

      const serverRes = await uploadRecording({
        name,
        aws: {
          isUploaded: true,
          location: result.data?.Location ?? "",
          key: result.data?.Key ?? "",
          bucket: result.data?.Bucket ?? "",
        },
        mediaType: isVideo ? "video" : "audio",
        size: recording.blob.size,
        duration: recording.elapsedTime,
        isAutoAnalyze: true,
        sourceLanguage,
        tags: [],
        ...(folderId ? { folderId } : {}),
      });

      if (serverRes.success && serverRes.data) {
        trackFunnel("in_app_recorder", "submit", {
          duration_seconds: recording.elapsedTime,
          file_size: recording.blob!.size,
        });
        trackFunnel("in_app_recorder", "recorder_submit", {
          mode,
          media_type: mode === "audio" ? "audio" : "video",
          duration_seconds: recording.elapsedTime,
          file_size: recording.blob!.size,
        });
        // null = malformed response; suppresses broken CTA link rather than showing it.
        setUploadedMediaId(serverRes.data.mediaId ?? null);
        setSubmitSuccess(true);
        baseRecording.clearRecordingAfterUpload();
        // The full take replaces the partial live copies; they are kept when there is no uploaded media to link to instead
        if (serverRes.data.mediaId) {
          for (const liveMediaId of takeMediaIdsRef.current) {
            deleteLiveMedia(liveMediaId, {
              onError: (err) =>
                Sentry.captureException(err, { tags: { feature: "recorder" }, extra: { liveMediaId } }),
            });
          }
        }
      } else {
        setSubmitError(t("UPLOAD.RECORD.REGISTER_FAILED", "Failed to register recording with server."));
      }
    } catch (err) {
      const error_reason =
        err instanceof Error ? err.message : "Failed to upload recording.";
      trackFailure("in_app_recorder", "fail", { error_reason });
      // DOMException permission errors are user-caused and already tracked above; only send unexpected errors to Sentry.
      if (!(err instanceof DOMException)) {
        Sentry.captureException(err, {
          tags: { feature: "recorder" },
          extra: { error_reason: error_reason.slice(0, 500) },
        });
      }
      setSubmitError(error_reason);
    } finally {
      setIsSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording.blob, recording.elapsedTime, user, subscription?.bucketId, mode, isVideo, recordingName, folderId, sourceLanguage, deleteLiveMedia]);

  const viewMediaId = uploadedMediaId ?? liveTranscript.mediaData?.mediaId ?? null;
  const viewFolderId = uploadedMediaId ? folderId : (liveTranscript.mediaData?.folderId ?? folderId);
  const showModeTabs = !isRecording && !isReadyToSubmit;
  const showLiveSettings = mode === "audio" && !isRecording && !isReadyToSubmit;

  const liveSettings = showLiveSettings ? (
    isFolderLoading ? (
      <Skeleton className="h-11 w-full max-w-[560px]" aria-label={t("RECORD.LOADING_FOLDERS", "Loading folders…")} />
    ) : (
      <>
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm">
          <Switch
            id="live-transcription"
            checked={isLiveTranscription}
            onChange={(checked) => {
              setIsLiveTranscription(checked);
              if (!checked) {
                liveTranscript.clearWords();
              }
            }}
            data-testid="live-transcription-toggle"
          />
          <label htmlFor="live-transcription" className="font-medium text-foreground">
            {t("RECORD.LIVE_TRANSCRIPTION", "Live transcription")}
          </label>
          <span className="text-xs text-muted-foreground">
            · {t("RECORD.LIVE_TRANSCRIPTION_HINT", "Transcribe as you record")}
          </span>
        </div>

        {isLiveTranscription && (
          <div className="grid w-full max-w-[560px] gap-4 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-2">
              <label htmlFor="record-folder" className="text-xs font-medium text-muted-foreground">
                {t("RECORD.FOLDER", "Save to folder")}
              </label>
              <div className="w-full" data-sentry-mask data-testid="record-folder-select">
                <FolderSelect
                  id="record-folder"
                  className="min-h-11"
                  value={folderId}
                  onChange={setFolderId}
                  placeholder={t("RECORD.FOLDER_PLACEHOLDER", "Default")}
                />
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <label htmlFor="record-language" className="text-xs font-medium text-muted-foreground">
                {t("RECORD.LANGUAGE", "Source language")}
              </label>
              <LanguageSelect
                id="record-language"
                className="w-full min-h-11"
                value={sourceLanguage}
                onChange={setSourceLanguage}
                filterLive
                data-testid="record-language-input"
              />
            </div>
          </div>
        )}

        {isLiveTranscription && (liveTranscript.error || liveTokenError) && (
          <div
            role="alert"
            className="w-full max-w-[560px] rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-danger"
            data-testid="live-transcription-error"
          >
            {liveTranscript.error ??
              t(
                "RECORD.LIVE_TRANSCRIPTION_KEY_ERROR",
                "Couldn't start live transcription. Please try again."
              )}
          </div>
        )}
      </>
    )
  ) : null;

  const modeSwitch = showModeTabs ? (
    <div
      role="group"
      aria-label={t("RECORD.MODE_LABEL", "Recording type")}
      className="flex min-w-0 flex-1 items-center gap-1 rounded-lg border border-border bg-muted/50 p-1 sm:inline-flex sm:flex-none"
    >
      {RECORD_MODES.filter((option) => option.mode !== "screen" || screenSupported).map((option) => {
        const isSupported = browserSupport[option.supportKey];
        const isActive = mode === option.mode;
        const modeLabel = t(option.labelKey, option.label);
        const Icon = option.icon;
        return (
          // Disabled buttons get pointer-events-none, so the hover hint lives on the wrapper.
          <span
            key={option.mode}
            className="inline-flex flex-1 sm:flex-none"
            title={isSupported ? undefined : t("RECORD.MODE_UNSUPPORTED", "Not supported in this browser")}
          >
            <button
              type="button"
              aria-pressed={isActive}
              disabled={!isSupported}
              onClick={() => handleModeClick(option.mode)}
              aria-label={isSupported
                ? undefined
                : t("RECORD.MODE_UNSUPPORTED_NAMED", "{{mode}}, not supported in this browser", { mode: modeLabel })}
              className={cn(
                "inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                "disabled:pointer-events-none disabled:opacity-50",
                isActive ? "bg-card font-semibold text-primary shadow-xs" : "text-muted-foreground hover:text-foreground",
              )}
              data-testid={`record-mode-${option.mode}`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {modeLabel}
            </button>
          </span>
        );
      })}
    </div>
  ) : null;

  const recorderControls = (
    <MediaRecorderControls
      recording={recording}
      mode={mode}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      recordingName={recordingName}
      onRecordingNameChange={setRecordingName}
      folderId={folderId}
      onFolderChange={setFolderId}
      sourceLanguage={sourceLanguage}
      onLanguageChange={setSourceLanguage}
      previewStream={previewStream}
      hasPermission={hasPermission}
      onRequestPermission={handleRequestPermission}
      videoQuality={videoQuality}
      onQualityChange={setVideoQuality}
      submitError={submitError}
      transcriptParagraphs={liveTranscript.paragraphs}
      isTranscriptConnected={liveTranscript.isConnected}
      isTranscriptComplete={liveTranscript.isComplete}
      transcriptError={liveTranscript.error}
      isLiveTranscription={isLiveTranscription}
      uploadProgress={uploadProgress}
      withCameraOverlay={withCameraOverlay}
      onWithCameraOverlayChange={setWithCameraOverlay}
      cameraStream={null}
      audioOutputDevices={baseRecording.audioOutputDevices.map((d) => ({ id: d.deviceId, label: d.label }))}
      selectedOutputDevice={baseRecording.selectedOutputDevice}
      onOutputDeviceChange={(id) => {
        baseRecording.setSelectedOutputDevice(id);
        void baseRecording.setSinkIdOnElement(mediaRef.current, id);
      }}
      onSwitchCamera={mode === "video" ? () => {
        // Cycling selectedVideoDevice triggers the preview useEffect which re-acquires the stream.
        const devices = baseRecording.videoDevices;
        if (devices.length > 1) {
          const idx = devices.findIndex((d) => d.deviceId === baseRecording.selectedVideoDevice);
          const next = devices[(idx + 1) % devices.length];
          baseRecording.setSelectedVideoDevice(next.deviceId);
        }
        baseRecording.switchCameraPosition();
      } : undefined}
      mediaRef={mediaRef as RefObject<HTMLAudioElement | HTMLVideoElement | null>}
      onAttachMic={mode === "audio" ? baseRecording.attachWavesurferMicrophone : undefined}
      onDetachMic={mode === "audio" ? baseRecording.detachWavesurferMicrophone : undefined}
      settingsSlot={liveSettings}
      modeSwitch={modeSwitch}
      liveFolderName={folders.find((f) => f.folderId === (liveTranscript.mediaData?.folderId ?? liveFolderId))?.name}
    />
  );

  return (
    <div className="space-y-6">
      {!isDialog && (
        <PageHeader
          title={t("UPLOAD.RECORD.DOCUMENT_TITLE", "Record")}
          description={t("RECORD.PAGE_SUBTITLE", "Record audio, video or your screen. We transcribe it when you upload.")}
        />
      )}

      <div className="flex flex-col gap-4">
        {submitSuccess && (
          <div
            className="rounded-xl border border-success/30 bg-success/5 p-6"
            data-testid="record-success"
          >
            <div className="flex items-center gap-2">
              <CircleCheck className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
              <h2 className="text-lg font-medium text-foreground">
                {t("RECORD.SUCCESS_TITLE", "Recording uploaded")}
              </h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(
                "RECORD.SUCCESS_DESC",
                "Your recording has been uploaded and is being processed."
              )}
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
              {viewMediaId && (
                <Button
                  onClick={isDialog
                    ? () => onNavigate?.("file", viewMediaId, viewFolderId)
                    : () => navigate(
                      viewFolderId
                        ? `/folder/${encodeURIComponent(viewFolderId)}/insight/${encodeURIComponent(viewMediaId)}`
                        : "/home"
                    )}
                  size="sm"
                  data-testid="record-view-recording"
                >
                  {t("RECORD.VIEW_RECORDING", "View recording")}
                </Button>
              )}
              <Button
                onClick={() => {
                  setSubmitSuccess(false);
                  setSubmitError(null);
                  setRecordingName("");
                  setUploadedMediaId(null);
                  liveTranscript.clearWords();
                }}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="record-record-again"
              >
                <Mic className="h-4 w-4" aria-hidden="true" />
                {t("RECORD.RECORD_ANOTHER", "Record another")}
              </Button>
              <Button
                onClick={isDialog
                  ? () => onNavigate?.("dashboard")
                  : () => navigate("/home")}
                variant="ghost"
                size="sm"
                data-testid="record-go-to-dashboard"
              >
                {t("RECORD.GO_TO_DASHBOARD", "Go to dashboard")}
              </Button>
            </div>
          </div>
        )}

        {!submitSuccess && (
          <>
            {submitError && (
              <div
                className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
                data-testid="record-error"
              >
                {submitError}
              </div>
            )}

            {showMaxDurationWarning && (
              <div
                role="alert"
                className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning"
                data-testid="max-duration-warning"
              >
                {t(
                  "RECORD.MAX_DURATION_WARNING",
                  "Recording will stop automatically in less than 1 minute (4-hour limit). Save now or stop manually to keep this recording.",
                )}
              </div>
            )}

            {showModeTabs && (
              <RecoverTakeBanner
                takes={savedTakes}
                busyTakeId={busyTakeId}
                onUpload={handleRecoverUpload}
                onDownload={handleRecoverDownload}
                onDiscard={handleRecoverDiscard}
              />
            )}

            <div
              className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-4 sm:p-6"
              data-testid="record-card"
            >
              {recorderControls}
            </div>
          </>
        )}

        {!isRecording && !isSubmitting && (
          <PhoneAppNote
            surface="app-record-page"
            phoneCopy={{
              i18nKey: "RECORD.PHONE_APP_NOTE_ON_PHONE",
              defaults: "Recording on your phone? The app keeps recording when your screen locks. <0>Get the app</0>",
            }}
          />
        )}
      </div>

      <ConfirmDialog
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
        onConfirm={() => {
          setShowDiscardDialog(false);
          trackFunnel("in_app_recorder", "reset", { mode });
          if (baseRecording.isRecording) baseRecording.stopRecording();
          baseRecording.discardRecording();
          setRecordingName("");
        }}
        title={t("RECORD.DISCARD_RECORDING_TITLE", "Discard this recording?")}
        description={t("RECORD.DISCARD_RECORDING_DESC", "This take will be removed.")}
        cancelLabel={t("ACTION.CANCEL", "Cancel")}
        confirmLabel={t("ACTION.DISCARD", "Discard")}
        variant="danger"
        data-testid="discard-dialog"
      />
    </div>
  );
}

export function UploadRecordPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("UPLOAD.RECORD.DOCUMENT_TITLE", "Record"));
  return <RecordPageContent />;
}
